import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Search, Settings, AlertCircle } from 'lucide-react';
import { GSCSummaryCards } from '../components/search-console/GSCSummaryCards';
import { GSCDailyChart } from '../components/search-console/GSCDailyChart';
import { GSCKeywordsTable, GSCSidebar, GSCPagesTable } from '../components/search-console/GSCDataPanels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchConsole() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [days, setDays] = useState(30);
  const [keywords, setKeywords] = useState([]);
  const [pages, setPages] = useState([]);
  const [daily, setDaily] = useState([]);
  const [devices, setDevices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [configOpen, setConfigOpen] = useState(false);
  const [configClientId, setConfigClientId] = useState('');
  const [configSiteUrl, setConfigSiteUrl] = useState('');
  const [sortField, setSortField] = useState('clicks');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    fetchClients();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data);
    } catch (e) {
      logger.warn && console.warn('Failed to fetch clients:', e);
    }
  };

  const fetchData = async (clientId, period) => {
    if (!clientId) return;
    setLoading(true);
    setError('');
    try {
      const [kw, pg, dl, dv, ct] = await Promise.all([
        axios.get(`${API}/gsc/client/${clientId}/keywords?days=${period}&limit=50`, { withCredentials: true }),
        axios.get(`${API}/gsc/client/${clientId}/pages?days=${period}`, { withCredentials: true }),
        axios.get(`${API}/gsc/client/${clientId}/daily?days=${period}`, { withCredentials: true }),
        axios.get(`${API}/gsc/client/${clientId}/devices?days=${period}`, { withCredentials: true }),
        axios.get(`${API}/gsc/client/${clientId}/countries?days=${period}`, { withCredentials: true }),
      ]);
      setKeywords(kw.data);
      setPages(pg.data);
      setDaily(dl.data);
      setDevices(dv.data);
      setCountries(ct.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur lors du chargement';
      setError(msg);
      setKeywords([]); setPages([]); setDaily([]); setDevices([]); setCountries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (clientId) => {
    setSelectedClientId(clientId);
    const client = clients.find(c => c.id === clientId);
    if (client && !client.gsc_site_url) {
      setError('');
      openConfig(clientId);
    } else {
      fetchData(clientId, days);
    }
  };

  const handleDaysChange = (d) => {
    setDays(d);
    if (selectedClientId) fetchData(selectedClientId, d);
  };

  const openConfig = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setConfigClientId(clientId);
    setConfigSiteUrl(client?.gsc_site_url || '');
    setConfigOpen(true);
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API}/gsc/client/${configClientId}/site`, {
        gsc_site_url: configSiteUrl,
      }, { withCredentials: true });
      setConfigOpen(false);
      const { data: updated } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(updated);
      if (configSiteUrl.trim() && configClientId === selectedClientId) {
        fetchData(selectedClientId, days);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de configuration');
    }
  };

  // Summary stats
  const totals = useMemo(() => {
    if (!keywords.length) return null;
    const totalClicks = keywords.reduce((s, k) => s + k.clicks, 0);
    const totalImpressions = keywords.reduce((s, k) => s + k.impressions, 0);
    const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const avgPosition = keywords.reduce((s, k) => s + k.position, 0) / keywords.length;
    return {
      clicks: totalClicks,
      impressions: totalImpressions,
      ctr: avgCtr.toFixed(2),
      position: avgPosition.toFixed(1),
      keywordCount: keywords.length,
    };
  }, [keywords]);

  // Sorted keywords
  const sortedKeywords = useMemo(() => {
    return [...keywords].sort((a, b) => {
      const m = sortDir === 'desc' ? -1 : 1;
      return (a[sortField] - b[sortField]) * m;
    });
  }, [keywords, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const formatDate = (d) => {
    const parts = d.split('-');
    return `${parts[2]}/${parts[1]}`;
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);

  return (
    <Layout>
      <div className="space-y-6" data-testid="gsc-page">
        {/* Header */}
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
            Search Console
          </h1>
          <p className="text-neutral-500 mt-1">Impressions, clics et CTR par mot-clé</p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex-1 max-w-xs">
            <Select value={selectedClientId} onValueChange={handleClientChange}>
              <SelectTrigger data-testid="gsc-client-select">
                <SelectValue placeholder="Sélectionner un client..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} {c.gsc_site_url ? '' : '(non configuré)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            {[7, 30, 90].map(d => (
              <Button
                key={d}
                variant={days === d ? 'default' : 'outline'}
                size="sm"
                className={days === d ? 'bg-[#002FA7] hover:bg-[#002277]' : ''}
                onClick={() => handleDaysChange(d)}
                data-testid={`gsc-days-${d}`}
              >
                {d}j
              </Button>
            ))}
          </div>
          {selectedClientId && (
            <Button variant="outline" size="sm" onClick={() => openConfig(selectedClientId)} data-testid="gsc-config-btn">
              <Settings className="w-4 h-4 mr-2" />
              Configurer
            </Button>
          )}
        </div>

        {/* No client */}
        {!selectedClientId && (
          <div className="bg-white border border-neutral-200 p-12 text-center">
            <Search className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <p className="text-neutral-500">Sélectionnez un client pour voir ses données Search Console</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className={`border rounded p-4 flex items-start gap-3 ${error.includes('non configuré') ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid="gsc-error">
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${error.includes('non configuré') ? 'text-amber-600' : 'text-red-600'}`} />
            <div>
              <p className={`text-sm font-medium ${error.includes('non configuré') ? 'text-amber-800' : 'text-red-800'}`}>
                {error.includes('non configuré') ? 'Configuration requise' : 'Erreur de chargement'}
              </p>
              <p className={`text-sm mt-1 ${error.includes('non configuré') ? 'text-amber-700' : 'text-red-700'}`}>{error}</p>
              {selectedClientId && error.includes('non configuré') && (
                <Button variant="outline" size="sm" className="mt-3" onClick={() => openConfig(selectedClientId)}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configurer le site
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
            <p className="text-neutral-500 text-sm mt-3">Chargement des données Search Console...</p>
          </div>
        )}

        {/* Data Display */}
        {totals && !loading && (
          <>
            <GSCSummaryCards totals={totals} />
            <GSCDailyChart daily={daily} clientName={selectedClient?.name} formatDate={formatDate} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <GSCKeywordsTable keywords={sortedKeywords} sortField={sortField} sortDir={sortDir} onToggleSort={toggleSort} clientId={selectedClientId} clientName={selectedClient?.name} />
              <GSCSidebar devices={devices} countries={countries} />
            </div>

            <GSCPagesTable pages={pages} />
          </>
        )}

        {/* Config Dialog */}
        <Dialog open={configOpen} onOpenChange={setConfigOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#002FA7]" />
                Configurer Search Console
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-neutral-500">
                Entrez l'URL du site telle qu'elle apparaît dans Google Search Console (ex: https://example.com/ ou sc-domain:example.com).
              </p>
              <div>
                <Label htmlFor="site-url">URL du site</Label>
                <Input
                  id="site-url"
                  value={configSiteUrl}
                  onChange={(e) => setConfigSiteUrl(e.target.value)}
                  placeholder="https://example.com/"
                  className="mt-1 font-mono"
                  data-testid="gsc-site-input"
                />
              </div>
              <div className="bg-neutral-50 rounded p-3 text-xs text-neutral-500 space-y-1">
                <p className="font-medium text-neutral-700">N'oubliez pas :</p>
                <p>Ajoutez le compte de service comme <strong>utilisateur</strong> dans Search Console :</p>
                <p className="font-mono text-[#002FA7] break-all">seo-platform-analytics@projetmanagementseo.iam.gserviceaccount.com</p>
                <p className="mt-2">Dans Search Console &gt; Paramètres &gt; Utilisateurs et autorisations &gt; Ajouter un utilisateur (rôle : Complet ou Restreint)</p>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setConfigOpen(false)}>Annuler</Button>
                <Button
                  className="bg-[#002FA7] hover:bg-[#002277]"
                  onClick={handleSaveConfig}
                  disabled={!configSiteUrl.trim()}
                  data-testid="gsc-save-btn"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
